import { definedMap } from "@glideapps/ts-necessities";

import { SerializedComponent } from "../../components/SerializedApp";
import {
    ArrayScreenFormat,
    ScreenDescriptionKind,
    PropertyDescription,
    InputOutputTables,
    TableGlideType,
    getPrimitiveNonHiddenColumns,
    MutatingScreenKind,
} from "../description";
import { PropertyDescriptor, PropertyTableGetter, ComponentBuilder, ComponentConfiguratorContext } from "../components";
import { ImageAspectRatio, TilesPadding, TextPosition } from "../components/image-types";
import {
    SummaryArrayScreenDescription,
    FallbackInitialsSummaryArrayContentDescription,
    SummaryArrayScreenHandler,
} from "./summary-array-screen";
import {
    makeImageHeightPropertyHandler,
    EnumPropertyHandler,
    ColumnPropertyHandler,
    ColumnPropertyFlag,
} from "../components/property-handlers";
import { TextStyle } from "../components/text-types";
import { handleTruncationAndGrouping } from "./truncation-lib";
import { makeEmptyMessage, InlineListBuild } from "./array-content";
import {
    getOverlayPropertyHandlers,
    hasFavoriteButton,
    OverlayContentDescription,
    getOverlayComponents,
    getOverlaySearchProperties,
} from "../overlay-utils";
import {
    isHorizontalPropertyHandler,
    textSizePropertyHandler,
    allCapsPropertyHandler,
    cornersPropertyHandler,
    pushHorizontalAndTruncatePropertyHandlers,
    truncateListPropertyHandler,
} from "./properties";
import { PropertySection } from "../components/property-sections";
import { getColumnProperty } from "../components/property-utils";
import { getPropertyWithColumnFormat } from "../formulas";
import { CardFloatStyle } from "../components/card-types";

export interface CardsArrayContentDescription
    extends FallbackInitialsSummaryArrayContentDescription,
        OverlayContentDescription {
    readonly header: PropertyDescription | undefined;
    readonly size: PropertyDescription;
    readonly numColumns: PropertyDescription;
    readonly isHorizontal: PropertyDescription;
    readonly numTextLines: PropertyDescription;
    readonly floatStyle: PropertyDescription;
    readonly corners: PropertyDescription;
    readonly textSize: PropertyDescription;
    readonly allCaps: PropertyDescription;
    readonly otherCaption: PropertyDescription | undefined;
}

interface CardsArrayScreenDescription extends SummaryArrayScreenDescription, CardsArrayContentDescription {
    readonly kind: ScreenDescriptionKind.Array;
    readonly format: ArrayScreenFormat.Cards;
}

const defaultSize = ImageAspectRatio.ThreeByOne;
const sizePropertyHandler = makeImageHeightPropertyHandler(defaultSize, false, false, true, "Image shape");

const numberOfColumnsPropertyHandler = new EnumPropertyHandler(
    { numColumns: 1 },
    "",
    "Size",
    [
        {
            value: 1,
            label: "Full-Size",
            icon: "fullSizeCard",
        },
        {
            value: 2,
            label: "Half-Size",
            icon: "halfSizeCard",
        },
    ],
    PropertySection.Design,
    "small-images"
);

const numberOfTextLinesPropertyHandler = new EnumPropertyHandler(
    { numTextLines: 3 },
    "",
    "Lines of text",
    [
        { value: 1, label: "1 line of text" },
        { value: 2, label: "2 lines of text" },
        { value: 3, label: "3 lines of text" },
        { value: 4, label: "4 lines of text" },
        { value: 5, label: "5 lines of text" },
        { value: 6, label: "6 lines of text" },
    ],
    PropertySection.TextStyle,
    "slider"
);

const floatStylePropertyHandler = new EnumPropertyHandler(
    { floatStyle: CardFloatStyle.Float },
    "",
    "Card style",
    [
        { value: CardFloatStyle.Float, label: "Float", icon: "cardFloat" },
        { value: CardFloatStyle.Outline, label: "Outline", icon: "cardOutline" },
        { value: CardFloatStyle.None, label: "None", icon: "cardNone" },
    ],
    PropertySection.Design,
    "small-images"
);

function isHorizontal(desc: CardsArrayContentDescription): boolean {
    return isHorizontalPropertyHandler.getEnum(desc);
}

export class CardsArrayScreenHandler extends SummaryArrayScreenHandler<
    CardsArrayContentDescription,
    CardsArrayScreenDescription
> {
    protected readonly supportsNonURLImages = true;
    protected readonly supportsEmojiImages = false;
    protected readonly fullTitleFallback = true;
    // We only support optional truncation when vertical
    protected readonly supportsTruncateList = false;

    constructor() {
        super(ArrayScreenFormat.Cards, "Cards", "cards", false);
    }

    protected titleRequired() {
        return false;
    }

    protected get imagePropertyFirst(): boolean {
        return true;
    }

    protected get supportsGrouping(): boolean {
        return true;
    }

    // FIXME: Unify with Tiles
    public getFavoritePivots(
        desc: CardsArrayContentDescription,
        table: TableGlideType
    ): ReadonlyMap<TableGlideType, string | true> | undefined {
        if (!hasFavoriteButton(desc, undefined)) return undefined;

        return new Map([[table, true]]);
    }

    protected content(
        builder: ComponentBuilder,
        desc: CardsArrayContentDescription,
        table: TableGlideType,
        arrayData: SerializedComponent,
        title: SerializedComponent | undefined,
        subtitle: SerializedComponent | undefined,
        image: SerializedComponent | undefined,
        onTap: SerializedComponent | undefined,
        onDropImage: SerializedComponent | undefined,
        searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        _uniqueID: string,
        caption: SerializedComponent | undefined,
        itemID: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;
        const header = definedMap(getColumnProperty(desc.header), n => getPropertyWithColumnFormat(builder, table, n));
        const size = b.constant(sizePropertyHandler.getEnum(desc));
        const numColumnsRaw = numberOfColumnsPropertyHandler.getEnum(desc);
        const numColumns = b.constant(numColumnsRaw);
        const cornersRaw = cornersPropertyHandler.getEnum(desc);
        const corners = b.constant(cornersRaw);
        const textSizeRaw = textSizePropertyHandler.getEnum(desc);
        const textSize = b.constant(textSizeRaw);
        const allCaps = allCapsPropertyHandler.getSwitch(desc);
        const numTextLines = b.constant(numberOfTextLinesPropertyHandler.getEnum(desc));
        const floatStyle = b.constant(floatStylePropertyHandler.getEnum(desc));
        const textStyle = b.constant(allCaps ? TextStyle.AllCaps : TextStyle.Normal);
        const otherCaption = definedMap(getColumnProperty(desc.otherCaption), n =>
            getPropertyWithColumnFormat(builder, table, n)
        );
        const emptyMessage = makeEmptyMessage(b, searchActiveFlag, inlineListBuild);

        const [topLeftOverlay, topRightOverlay, bottomLeftOverlay] = getOverlayComponents(
            desc,
            builder,
            table,
            undefined,
            false,
            cornersRaw,
            textSizeRaw
        );
        let bottomRightOverlay: SerializedComponent | undefined;
        if (otherCaption !== undefined) {
            bottomRightOverlay = b.avatarOverlay(undefined, otherCaption, textSize);
        }

        const horizontal = isHorizontal(desc);
        const cardItem = b.ui("CardItem", {
            title,
            subtitle,
            header,
            image,
            size,
            floatStyle,
            onTap,
            onDropImage,
            numTextLines,
            corners,
            textSize,
            textStyle,
            isHorizontal: b.constant(horizontal),
            itemID,
            topLeftOverlay: topLeftOverlay,
            topRightOverlay: topRightOverlay,
            bottomLeftOverlay: bottomLeftOverlay,
            bottomRightOverlay: bottomRightOverlay,
        });

        if (horizontal) {
            return handleTruncationAndGrouping(
                builder,
                desc,
                table,
                arrayData,
                inlineListBuild,
                caption,
                numColumnsRaw,
                true,
                (listData, isGrouped, args) =>
                    b.ui("HorizontalTiles", {
                        list: listData,
                        numColumns,
                        caption,
                        isGrouped,
                        item: cardItem,
                        emptyMessage,
                        ...args,
                    })
            );
        } else {
            const padding = b.constant(TilesPadding.Loose);
            return handleTruncationAndGrouping(
                builder,
                desc,
                table,
                arrayData,
                inlineListBuild,
                caption,
                numColumnsRaw,
                true,
                (listData, isGrouped, args) =>
                    b.ui("VerticalTiles", {
                        list: listData,
                        numColumns,
                        padding,
                        isGrouped,
                        emptyMessage,
                        item: cardItem,
                        allowWrapping: b.constant(true),
                        textPosition: b.constant(TextPosition.Below),
                        caption,
                        ...args,
                    })
            );
        }
    }

    public randomContentDescription(
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext,
        insideInlineList: boolean,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean
    ): CardsArrayContentDescription | undefined {
        const desc = this.defaultContentDescription(
            tables,
            ccc,
            insideInlineList,
            mutatingScreenKind,
            isDefaultArrayScreen
        );
        if (desc === undefined) return undefined;

        const size = sizePropertyHandler.getRandom();
        const numberOfColumns = numberOfColumnsPropertyHandler.getRandom();
        const numberOfTextLines = numberOfTextLinesPropertyHandler.getRandom();
        const floatStyle = floatStylePropertyHandler.getRandom();
        const corners = cornersPropertyHandler.getRandom();
        const textSize = textSizePropertyHandler.getRandom();
        const allCaps = allCapsPropertyHandler.getRandom();

        return {
            ...desc,
            ...sizePropertyHandler.getProperty(size),
            ...numberOfColumnsPropertyHandler.getProperty(numberOfColumns),
            ...numberOfTextLinesPropertyHandler.getProperty(numberOfTextLines),
            ...floatStylePropertyHandler.getProperty(floatStyle),
            ...cornersPropertyHandler.getProperty(corners),
            ...textSizePropertyHandler.getProperty(textSize),
            ...allCapsPropertyHandler.getProperty(allCaps),
        };
    }

    public adjustContentDescriptionAfterUpdate(
        desc: CardsArrayContentDescription,
        updates: Partial<CardsArrayContentDescription> | undefined
    ): CardsArrayContentDescription {
        const numColumnsRaw = numberOfColumnsPropertyHandler.getEnum(desc);
        if (numColumnsRaw < 1 || numColumnsRaw > 2) {
            desc = numberOfColumnsPropertyHandler.setInDescription(
                desc,
                numberOfColumnsPropertyHandler.defaultCaseValue
            ) as CardsArrayContentDescription;
        }

        if (updates?.isHorizontal !== undefined && isHorizontalPropertyHandler.getEnum(desc)) {
            desc = truncateListPropertyHandler.setInDescription(desc, truncateListPropertyHandler.defaultValue);
        }

        return desc;
    }

    protected getInterSummaryPropertyDescriptors<T extends CardsArrayContentDescription>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        _insideInlineList: boolean
    ): readonly PropertyDescriptor[] {
        return [
            new ColumnPropertyHandler(
                "header",
                "Header",
                [ColumnPropertyFlag.Optional, ColumnPropertyFlag.Editable, ColumnPropertyFlag.Searchable],
                getPropertyTable,
                ["header"],
                getPrimitiveNonHiddenColumns,
                "string",
                PropertySection.Data
            ),
        ];
    }

    public getContentPropertyDescriptors<T extends CardsArrayContentDescription>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        insideInlineList: boolean,
        desc: T,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean,
        withTransforms: boolean
    ): ReadonlyArray<PropertyDescriptor> {
        const descrs: PropertyDescriptor[] = [
            ...super.getContentPropertyDescriptors(
                getPropertyTable,
                insideInlineList,
                desc,
                ccc,
                mutatingScreenKind,
                isDefaultArrayScreen,
                withTransforms
            ),
        ];

        descrs.push(sizePropertyHandler, numberOfColumnsPropertyHandler, floatStylePropertyHandler);

        pushHorizontalAndTruncatePropertyHandlers(descrs, desc, isDefaultArrayScreen, true);

        descrs.push(cornersPropertyHandler, textSizePropertyHandler, numberOfTextLinesPropertyHandler);

        const otherCaptionPropertyHandler = new ColumnPropertyHandler(
            "otherCaption",
            "Caption",
            [ColumnPropertyFlag.Editable, ColumnPropertyFlag.Searchable, ColumnPropertyFlag.EmptyByDefault],
            getPropertyTable,
            ["caption", "description"],
            getPrimitiveNonHiddenColumns,
            "string",
            PropertySection.Overlays
        );

        descrs.push(
            allCapsPropertyHandler,
            ...getOverlayPropertyHandlers(getPropertyTable, undefined, "Avatar text"),
            otherCaptionPropertyHandler
        );

        return descrs;
    }

    public getBasicSearchProperties(desc: CardsArrayContentDescription): readonly string[] {
        const properties = [...super.getBasicSearchProperties(desc), ...getOverlaySearchProperties(desc)];
        const otherCaption = getColumnProperty(desc.otherCaption);
        if (otherCaption !== undefined) {
            properties.push(otherCaption);
        }
        return properties;
    }
}
